import { useCopyToClipboard } from '@uidotdev/usehooks';
import { memo, useMemo } from 'react';

import { Button, Text } from '@/components/core';
import { ArrowShortIcon, CopyIcon, DeleteIcon } from '@/components/icons';
import { useFocusNext } from '@/hooks';
import { useDocumentStore } from '@/store';
import { Point } from '@/utils';

export type OcrPopoverProps = {
  anchor: Point;
  text: string;
  onDismiss?: () => void;
};

const IOcrPopover = ({ anchor, text, onDismiss }: OcrPopoverProps) => {
  const focusedField = useDocumentStore((state) => state.focusedField);
  const hasFocusedField = useMemo(() => !!focusedField, [focusedField]);
  const buttonStyle = 'rounded-full justify-center items-center p-0.5 mr-1';
  const [_, copyToClipboard] = useCopyToClipboard();
  const focusNext = useFocusNext();

  return (
    <div
      className="absolute z-10 flex min-w-64 flex-col gap-2 rounded-lg bg-gray-900 p-2 shadow-lg"
      style={{ top: anchor.y, left: anchor.x }}
    >
      <textarea
        className="field-sizing-content w-full cursor-default resize-none rounded-sm bg-gray-800 p-1 text-sm font-medium text-gray-300 outline-none"
        readOnly
        value={text}
      />
      <div className="flex items-center gap-1">
        {hasFocusedField ? (
          <Text className="bg-success-900 text-medium text-success-300 ml-1 cursor-default rounded-xs px-0.5 text-xs">
            {focusedField?.name}
          </Text>
        ) : (
          <Text className="ml-1 cursor-default text-xs font-medium text-gray-300">Copy text to clipboard</Text>
        )}

        <div className="grow" />

        <Button variant="plain" className="focused:!bg-gray-800 hover:!bg-gray-800" onPress={onDismiss}>
          <DeleteIcon className="!text-gray-400" />
        </Button>

        {hasFocusedField ? (
          <Button
            variant="secondary"
            className={buttonStyle}
            onPress={() => {
              focusedField?.onChange?.(text);
              onDismiss?.();
              focusNext();
            }}
          >
            <ArrowShortIcon rotation="right" />
          </Button>
        ) : (
          <Button
            variant="secondary"
            className={buttonStyle}
            onPress={() => {
              copyToClipboard(text);
              onDismiss?.();
            }}
          >
            <CopyIcon />
          </Button>
        )}
      </div>
    </div>
  );
};

export const OcrPopover = memo(IOcrPopover) as typeof IOcrPopover;
